import { FC } from 'react';
import { ICustomerReview } from './types';
import styled from 'styled-components';
import { Rate } from 'antd';
import { Trans } from 'react-i18next';

export const StyledCard = styled.div`
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0px 4px 28px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 28px 21px;
  height: 381px;
  width: 245px;
  margin: 0 12px;
  gap: 17px;

  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    padding: 35px 21px;
    margin: 0 16px;
    height: 292px;
    width: 545px;
    gap: 9px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    gap: 12px;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
`;

const CardFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    gap: 7px;
  }
`;

const StarContainer = styled.div`
  display: flex;
  gap: 1px;
`;

const Star = styled(Rate)`
  color: #ff5722;
`;

const ReviewSummary = styled.h3`
  ${(props) => props.theme.typo.familyGoogle.semiBold};
  font-weight: 600;
  font-size: 14px;
  line-height: 1.57;
  text-align: center;
  color: #000000;
  margin: 0;

  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    font-size: 14px;
    line-height: 1.43;
  }
`;

const ReviewDetail = styled.blockquote`
  ${(props) => props.theme.typo.familyGoogle.regular};
  font-weight: 400;
  font-size: 14px;
  line-height: 1.57;
  text-align: center;
  color: #363636;
  margin: 0;
  height: 148px;
  padding-right: 8px;
  overflow-y: auto;

  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    padding-left: 32px;
    padding-right: 32px;
  }

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 4px;
    background-color: ${(props) => props.theme.color.webkitScrollBar};
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background: ${(props) => props.theme.color.webkitScrollBarThumb};
    border-color: ${(props) => props.theme.color.webkitScrollBarThumb};
  }

  @media screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    font-size: 14px;
    line-height: 1.5;
    height: 88px;
  }
`;

const ActionInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
`;

const SoldLabel = styled.span`
  ${(props) => props.theme.typo.familyGoogle.semiBold};
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  color: #3e3e3e;

  &.sub {
    font-weight: 400 !important;
    font-size: 10px;
    line-height: 1.6;
  }
`;

const CarModel = styled.span`
  ${(props) => props.theme.typo.familyGoogle.semiBold};
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  color: #ff5722;
`;

const ReviewSource = styled.p`
  ${(props) => props.theme.typo.familyGoogle.regular};
  font-weight: 400;
  font-size: 9px;
  line-height: 1.78;
  text-align: center;
  color: #8f90a3;
  margin: 0;
`;

interface IReviewCardProps {
  content: ICustomerReview;
}

const ReviewCard: FC<IReviewCardProps> = ({ content }) => {
  const { star, review, action, posted } = content;
  const { summary, detail } = review;

  return (
    <StyledCard role="article">
      <CardHeader>
        <StarContainer>
          <Star disabled defaultValue={star} aria-label={`${star} star rating`} />
        </StarContainer>
        <ReviewSummary>
          <Trans i18nKey={summary} defaults={summary} />
        </ReviewSummary>
      </CardHeader>

      <CardContent>
        <ReviewDetail>
          <Trans i18nKey={detail} defaults={detail} />
        </ReviewDetail>
      </CardContent>

      <CardFooter>
        <ActionInfo>
          <Trans
            i18nKey={action}
            defaults={action}
            components={{ label: <SoldLabel />, subLabel: <SoldLabel className="sub" />, model: <CarModel /> }}
          />
        </ActionInfo>
        <ReviewSource>
          <Trans i18nKey={posted} defaults={posted} />
        </ReviewSource>
      </CardFooter>
    </StyledCard>
  );
};

export default ReviewCard;
